import { Component } from 'react'
import { Router, ShippingOperationConfig, RemoteOperation, Sounds, AppStore } from 'stylewhere/shared'
import {
  askUserConfirmation,
  isModalError,
  hasChecklist,
  showToast,
  showToastError,
  canDeleteParcel
} from 'stylewhere/utils'
import {
  Shippings,
  ShippingParcel,
  AsnStatus,
  ParcelStatus,
  Asn,
  Asns,
  DecodedItem,
  ParcelContentEntry,
  Parcels,
  TmrProduct,
  ShippingParcelChecklist
} from 'stylewhere/api'
import type { Routes } from 'stylewhere/pages'
import { T, __ } from 'stylewhere/i18n'
import { ShippingExtensions } from 'stylewhere/extensions'
import {
  Box,
  Icons,
  Input,
  List,
  Page,
  ParcelRow,
  ParcelRightHeader,
  Button,
  Tab,
  ShippingProductTable
} from 'stylewhere/components'

export interface ShippingParcelStartParams {
  opCode: string
  asnId: string
}

export interface ShippingParcelStartState {
  // showForm: boolean
  searching: string
  parcels?: ShippingParcel[]
  loading: boolean
  error: boolean
  currentListPage: number
  totalPages: number
  totalElements: number
  loadingData: boolean
  asn?: Asn
  parcelReadings: DecodedItem<string>[]
  parcelReadingsByParcelId: { [parcelId: string]: DecodedItem<string>[] }
  loadingReadings: boolean
  currentTabOptions: { value: string; label: string; active: boolean }[]
  filteredProducts: { [upc: string]: TmrProduct }
  filteredChecklist: ShippingParcelChecklist | null
  filteredParcelReadings: DecodedItem<string>[]
}

interface LocationState {
  asn?: Asn
  filterParams?: Record<string, any>
}

const LIST_PAGE_SIZE = 25

export const getParcelReadingStatuses = (operationConfig: ShippingOperationConfig): ParcelStatus[] => {
  if (operationConfig.type === 'INBOUND') return ['DRAFT', 'IN_TRANSIT', 'FORCE_SENT', 'IN_INBOUND', 'INBOUND_ERROR']
  if (operationConfig.type === 'OUTBOUND') return ['DRAFT', 'PACKED', 'IN_OUTBOUND', 'OUTBOUND_ERROR']
  if (operationConfig.type === 'PACKING') return ['DRAFT']
  return []
}

export const getShipmentAddParcelStatuses = (operationConfig: ShippingOperationConfig): AsnStatus[] => {
  if (operationConfig.type === 'INBOUND') return ['DRAFT', 'IN_TRANSIT', 'IN_INBOUND', 'INBOUND_ERROR']
  if (operationConfig.type === 'OUTBOUND') return ['DRAFT', 'IN_TRANSIT', 'IN_OUTBOUND', 'INBOUND_ERROR']
  if (operationConfig.type === 'PACKING') return ['DRAFT']
  return []
}

interface ShippingParcelStartProps {
  match: { params: ShippingParcelStartParams }
  location: { state?: LocationState }
  // history: any
}

export default class ShippingParcelStart extends Component<ShippingParcelStartProps, ShippingParcelStartState> {
  asnId: string
  opCode: string
  isModal: boolean
  operation: ShippingOperationConfig
  backPath: Routes = '/shipping/:opCode'
  addParcelPath: Routes = '/shipping/:opCode/asn/:asnId/add'
  state: ShippingParcelStartState = {
    loading: false,
    rowLoading: {},
    rowExpanded: {},
    parcelsDetails: {},
    currentListPage: 0,
    totalElements: 0,
    totalPages: 0,
    loadingData: false,
    error: false,
    searching: '',
    parcelReadings: [] as DecodedItem<string>[],
    parcelReadingsByParcelId: {},
    loadingReadings: false,
    currentTabOptions: [
      { value: 'parcels', label: __(T.misc.parcels), active: true },
      { value: 'products', label: __(T.misc.products), active: false }
    ],
    filteredProducts: {},
    filteredChecklist: null,
    filteredParcelReadings: [] as DecodedItem<string>[]
  } as any

  initData: boolean = false
  parcelCount: number = 0
  parcelCounterCodes: string[] = []

  get locationState(): LocationState | undefined {
    return this.props.location.state
  }

  constructor(props: any) {
    super(props)
    const matchParams: ShippingParcelStartParams = this.props.match.params
    this.asnId = matchParams.asnId
    this.opCode = matchParams.opCode
    this.isModal = false
    this.operation = RemoteOperation.getOperationConfig<ShippingOperationConfig>(this.opCode)
  }

  canShowAddParcel() {
    const { asn } = this.state
    return (
      asn?.status &&
      getShipmentAddParcelStatuses(this.operation).includes(asn.status) &&
      this.operation.canCreateParcels &&
      this.operation.shippingMode === 'shipment'
    )
  }

  componentDidMount() {
    this.isModal = isModalError(this.operation)
    this.setState({ loading: true }, this.init)
  }

  componentWillUnmount() {
    this.initData = false
  }

  init = async () => {
    if (this.initData) return
    this.initData = true
    this.loadAsn(this.asnId)
  }

  async loadAsn(id: string) {
    try {
      let asn: Asn | undefined
      if (this.locationState?.asn) {
        asn = this.locationState.asn
      } else {
        const retrieveAsn = await Shippings.getAsnExtended(id)
        const formData = {
          shipmentCode: retrieveAsn.code,
          originPlaceId:
            retrieveAsn.originPlaceId && retrieveAsn.originPlaceId !== null
              ? retrieveAsn.originPlaceId
              : AppStore.defaultWorkstation?.placeId,
          destinationPlaceId:
            retrieveAsn.destinationPlaceId && retrieveAsn.destinationPlaceId !== null
              ? retrieveAsn.destinationPlaceId
              : AppStore.defaultWorkstation?.placeId,
          attributes: retrieveAsn.attributes ?? {}
        }
        asn = await Shippings.startAsn(this.operation, formData)
      }
      this.setState(
        { asn, filteredProducts: asn?.products ?? {}, filteredChecklist: asn?.checklist ?? null },
        this.loadAsnParcel
      )
    } catch (error) {
      showToastError(error, __(T.error.error), this.isModal)
    }
  }

  filterAsnParcel = async (search?: string) => {
    this.parcelCounterCodes = []
    this.setState(
      { loading: true, error: false, searching: search ?? '', parcels: [], currentListPage: 0 },
      this.loadAsnParcel
    )
  }

  private readonly matchesProductSearch = (product: TmrProduct, searchString: string): boolean => {
    return (
      product.code?.toLowerCase().includes(searchString) ||
      product.description?.toLowerCase().includes(searchString) ||
      product.style?.value?.toLowerCase().includes(searchString) ||
      product.material?.value?.toLowerCase().includes(searchString) ||
      product.color?.value?.toLowerCase().includes(searchString) ||
      product.size?.value?.toLowerCase().includes(searchString) ||
      false
    )
  }

  private readonly filterProductsBySearch = (products: TmrProduct[], searchString: string): TmrProduct[] => {
    if (!searchString) return products
    return products.filter((product) => this.matchesProductSearch(product, searchString))
  }

  private readonly filterChecklistBySearch = (
    checklist: ShippingParcelChecklist | null | undefined,
    searchString: string
  ): ShippingParcelChecklist | null => {
    if (!checklist) return null
    if (!searchString) return checklist

    const filteredChecklistProducts = checklist.checklistProducts?.filter((product) =>
      product.productCode.toLowerCase().includes(searchString)
    )
    const filteredChecklistItems = checklist.checklistItems?.filter((item) =>
      item.productCode.toLowerCase().includes(searchString)
    )

    return {
      ...checklist,
      checklistProducts: filteredChecklistProducts,
      checklistItems: filteredChecklistItems
    }
  }

  private readonly filterParcelReadingsBySearch = (
    readings: DecodedItem<string>[],
    searchString: string
  ): DecodedItem<string>[] => {
    if (!searchString) return readings
    return readings.filter((reading) => {
      const productCode = reading.item?.product?.code?.toLowerCase() ?? ''
      const identifierCode = reading.item?.identifiers?.[0]?.code?.toLowerCase() ?? ''
      return productCode.includes(searchString) || identifierCode.includes(searchString)
    })
  }

  filterAsnProduct = async (search?: string) => {
    const searchString = search?.toLowerCase() ?? ''
    const { asn, parcelReadings } = this.state

    const allProducts = Object.values(asn?.products ?? {})
    const filteredProducts = this.filterProductsBySearch(allProducts, searchString)
    const filteredProductsObject = Object.fromEntries(filteredProducts.map((p) => [p.code, p])) as {
      [upc: string]: TmrProduct
    }

    const filteredChecklist = this.filterChecklistBySearch(asn?.checklist, searchString)
    const filteredParcelReadings = this.filterParcelReadingsBySearch(parcelReadings, searchString)

    this.setState({
      filteredProducts: filteredProductsObject,
      filteredChecklist,
      filteredParcelReadings,
      loading: false
    })
  }

  singleParcelCounter = async (parcel: ShippingParcel) => {
    if (
      ['RECEIVED', 'READY_TO_RECEIVE', 'READY_TO_SEND', 'FORCE_RECEIVED', 'IN_TRANSIT', 'PACKED'].includes(
        parcel.status
      ) ||
      this.operation.persistParcelContentScannings === 'onBatchRead'
    ) {
      try {
        const entries: ParcelContentEntry[] = await Parcels.parcelContent(
          parcel.operationId,
          parcel.id,
          this.operation.readingsDisplayMode === 'item' ? 'item' : 'product'
        )
        parcel.contentEntries = entries
        parcel.parcelEntryQuantity = 0
        parcel.parcelUnexpectedQuantity = 0
        entries.forEach((e) => {
          parcel.parcelEntryQuantity += e.detected
          parcel.parcelUnexpectedQuantity += e.unexpected
        })
      } catch {
        console.log('error')
      }
    }
    return parcel
  }

  getParcelCounter = async (parcels: ShippingParcel[]) => {
    if (this.parcelCount < parcels.length) {
      if (!this.parcelCounterCodes.includes(parcels[this.parcelCount].code)) {
        this.parcelCounterCodes.push(parcels[this.parcelCount].code)
        const tmp = await this.singleParcelCounter(parcels[this.parcelCount])
        parcels[this.parcelCount] = tmp
      }
      this.parcelCount++
      return this.getParcelCounter(parcels)
    }
    return parcels
  }

  loadMore = () => {
    this.setState((prevState) => {
      return { loadingData: true, currentListPage: prevState.currentListPage + 1 }
    }, this.loadAsnParcel)
  }

  loadAsnParcel = async () => {
    const { currentListPage, searching, asn, parcels } = this.state
    try {
      const skip = !asn
      if (!skip) {
        const parcelsResponse = await Shippings.getParcelListByAsn(
          this.operation,
          asn.code,
          LIST_PAGE_SIZE,
          currentListPage,
          searching,
          this.operation.reverseParcelOrder ? 'asc' : 'desc',
          this.operation.filterByOperation
        )
        const { totalPages, totalElements } = parcelsResponse
        this.parcelCount = 0
        const data = await this.getParcelCounter(parcelsResponse.content ?? [])
        const tmp: ShippingParcel[] = (parcels ?? []).concat(data)

        this.setState(
          {
            loading: false,
            loadingData: false,
            parcels: tmp,
            totalPages,
            totalElements
          },
          () => {
            // Carica le letture dopo aver caricato le parcels
            this.initLoadAllParcelReadings()
          }
        )
      }
    } catch (error) {
      this.setState({ loading: false, loadingData: false, error: true })
      showToastError(error, __(T.error.error), this.isModal)
    }
  }

  private readonly enrichParcelEntriesWithChecklist = (
    entries: ParcelContentEntry[],
    checklist?: ShippingParcelChecklist
  ): void => {
    if (checklist) {
      for (const entry of entries) {
        const products = checklist.checklistProducts ?? []
        const findByProduct = products.find((pr) => pr.productCode === entry.product.code)
        if (findByProduct) {
          entry.expected += findByProduct.quantity
        }
        const items = checklist.checklistItems ?? []
        const findByItem = items.find((pr) => pr.productCode === entry.product.code)
        if (findByItem) {
          entry.expected += findByItem.quantity ?? 1
        }
      }
    }
  }

  private readonly convertParcelEntriesToDecodedItems = (entries: ParcelContentEntry[]): DecodedItem<string>[] => {
    return entries.map((entry) => ({
      item: {
        ...entry.item,
        // L'API parcelContent restituisce product a livello entry; groupItems (tab Prodotti) usa decodedItem.item.product
        product: (entry.product ?? entry.item.product) as TmrProduct
      },
      detected: entry.detected,
      expected: entry.expected,
      unexpected: entry.unexpected,
      identifierCode: entry.item?.identifiers?.[0]?.code,
      statuses: []
    }))
  }

  initLoadAllParcelReadings = async () => {
    const { parcels, asn } = this.state
    if (!parcels || parcels.length === 0 || !asn) {
      this.setState({ parcelReadings: [], parcelReadingsByParcelId: {}, filteredParcelReadings: [] })
      return
    }

    this.setState({ loadingReadings: true }, this.loadAllParcelReadings)
  }

  loadAllParcelReadings = async () => {
    const { parcels, asn } = this.state
    try {
      const allReadings: DecodedItem<string>[] = []
      const readingsByParcelId: { [parcelId: string]: DecodedItem<string>[] } = {}
      const isItems = this.operation.readingsDisplayMode === 'item'
      const groupedBy = isItems ? 'item' : 'product'

      // Quando checklistMode === 'shipment', carica le letture per ogni parcel separatamente
      // per poter escludere le letture di un parcel quando si visualizza un altro
      const shouldLoadByParcel = this.operation.options.checklistMode === 'shipment' && asn?.checklist

      if (shouldLoadByParcel) {
        // Carica le letture per ogni parcel separatamente
        for (const parcel of parcels) {
          const parcelEntries: ParcelContentEntry[] = await Parcels.parcelContent(
            this.operation.id,
            parcel.id,
            groupedBy
          )

          // Applica la logica simile a ParcelRow per aggiungere expected dal checklist
          this.enrichParcelEntriesWithChecklist(parcelEntries, asn?.checklist)

          // Converti ParcelContentEntry in DecodedItem
          const decodedItems = this.convertParcelEntriesToDecodedItems(parcelEntries)
          readingsByParcelId[parcel.id] = decodedItems
          allReadings.push(...decodedItems)
        }
      } else if (groupedBy === 'product') {
        const asnContentEntries: ParcelContentEntry[] = await Asns.asnContent(this.operation.id, asn.id, 'product')

        // Converti ParcelContentEntry in DecodedItem
        const decodedItems = this.convertParcelEntriesToDecodedItems(asnContentEntries)
        allReadings.push(...decodedItems)
      } else {
        // Per 'item' mode, carica i contenuti di tutte le parcels
        for (const parcel of parcels) {
          const parcelEntries: ParcelContentEntry[] = await Parcels.parcelContent(
            this.operation.id,
            parcel.id,
            groupedBy
          )

          // Applica la logica simile a ParcelRow per aggiungere expected dal checklist
          this.enrichParcelEntriesWithChecklist(parcelEntries, asn?.checklist)

          // Converti ParcelContentEntry in DecodedItem
          const decodedItems = this.convertParcelEntriesToDecodedItems(parcelEntries)
          allReadings.push(...decodedItems)
        }
      }

      this.setState({
        parcelReadings: allReadings,
        parcelReadingsByParcelId: readingsByParcelId,
        filteredParcelReadings: allReadings,
        loadingReadings: false
      })
    } catch (error) {
      showToastError(error, __(T.error.error), this.isModal)
      this.setState({
        parcelReadings: [],
        parcelReadingsByParcelId: {},
        filteredParcelReadings: [],
        loadingReadings: false
      })
    }
  }

  isParcelNotCompleted(parcel: ShippingParcel): boolean {
    return getParcelReadingStatuses(this.operation).includes(parcel.status)
  }

  onNewParcel = () => {
    const filterParams = this.locationState?.filterParams ?? {}
    Router.navigate(
      this.addParcelPath,
      { opCode: this.operation.code, asnId: this.asnId },
      {
        state: {
          asn: this.state.asn,
          filterParams: filterParams
        }
      }
    )
  }

  getOnConfirmShipmentCB = (askConfirmation: boolean) => async () => {
    const { asn } = this.state
    if (!asn) {
      showToastError(__(T.misc.shipment), __(T.error.error), this.isModal)
      return
    }

    try {
      if (askConfirmation) {
        const ok = await askUserConfirmation(
          __(T.confirm.confirm_close_asn_title),
          __(T.confirm.confirm_close_asn_text)
        )

        if (!ok) return
      }

      await Shippings.confirmAsn(this.operation, asn.code, {
        transactionDate: new Date().toISOString()
      })

      showToast({
        title: __(T.misc.success),
        description: __(T.messages.operation_success),
        status: 'success'
      })
      Sounds.success()

      Router.navigate(this.backPath, { opCode: this.operation.code })
    } catch (error) {
      showToastError(error, __(T.error.error), this.isModal)
    }
  }

  parcelRowRender = (parcel: ShippingParcel, _index?: number, _listRef?: any, onRowHeightChange?: () => void) => {
    const { asn, parcelReadingsByParcelId, parcels } = this.state
    if (this.operation.options.checklistMode === 'shipment' && asn?.checklist && parcel && !parcel?.checklist) {
      parcel.checklist = asn.checklist
      parcel.products = asn.products ?? {}
    }

    // Calcola le letture degli altri parcels (escluso quello corrente)
    let otherParcelsReadings: DecodedItem<string>[] = []
    if (this.operation.options.checklistMode === 'shipment' && asn?.checklist && parcelReadingsByParcelId && parcels) {
      otherParcelsReadings = parcels
        .filter((p) => p.id !== parcel.id)
        .flatMap((p) => parcelReadingsByParcelId[p.id] || [])
    }

    const filterParams = this.locationState?.filterParams ?? {}
    return (
      <ParcelRow
        expandable={!this.isParcelNotCompleted(parcel)}
        parcel={parcel}
        operation={this.operation}
        asn={this.state.asn}
        otherParcelsReadings={otherParcelsReadings.length > 0 ? otherParcelsReadings : undefined}
        onRowHeightChange={onRowHeightChange}
        canDelete={this.operation.canDeleteParcel && canDeleteParcel(parcel, this.operation)}
        onDelete={this.deleteFunc}
        filterParams={filterParams}
      />
    )
  }

  getAsnCounter = () => {
    const { parcels, asn } = this.state

    let expected
    let detected
    if (asn?.detectedCounter) {
      detected = asn.detectedCounter
    } else {
      detected =
        (parcels ?? []).reduce((acc, curr) => {
          return acc + (curr.parcelEntryQuantity ?? 0)
        }, 0) ?? 0
    }

    if (asn?.expectedCounter) {
      expected = hasChecklist(this.operation) ? asn.expectedCounter : undefined
    } else {
      expected = hasChecklist(this.operation)
        ? ((parcels ?? []).reduce((acc, curr) => {
            return acc + (curr.checklistExpectedQuantity ? curr.checklistExpectedQuantity : 0)
          }, 0) ?? 0)
        : undefined
    }

    const counter = {
      detected: detected,
      expected: expected
    }
    return counter
  }

  canDelete = () => {
    const { asn } = this.state
    return this.operation.canDeleteParcel && asn && ['IN_OUTBOUND', 'DRAFT', 'IN_INBOUND'].includes(asn.status)
  }

  deleteFunc = async (id: string, type: 'shipment' | 'parcel') => {
    const ok = await askUserConfirmation(
      type === 'shipment' ? __(T.title.delete_shipment_title) : __(T.custom.deleteSession),
      type === 'shipment' ? __(T.subtitle.delete_shipment_message) : __(T.custom.deleteSessionMessage)
    )
    if (ok) {
      const result = await Shippings._deleteEntity(id, type, this.operation)
      if (result.success) {
        showToast({
          title: __(T.misc.success),
          description: __(T.messages.operation_success),
          status: 'success'
        })
        Sounds.success()

        if (type === 'shipment') {
          Router.navigate(this.backPath, { opCode: this.operation.code })
        } else {
          this.setState(
            { currentListPage: 0, totalElements: 0, totalPages: 0, loading: true, searching: '', parcels: [] },
            this.loadAsnParcel
          )
        }
      } else {
        showToastError(result.message, __(T.misc.error), this.isModal)
      }
    }
  }

  onTabClick = (tab: string) => {
    this.setState((prevState) => ({
      currentTabOptions: prevState.currentTabOptions.map((opt) => ({ ...opt, active: opt.value === tab }))
    }))
  }

  goBack = () => {
    const filterParams = this.locationState?.filterParams ?? {}
    if (this.operation.templatePath === 'withEncoding') {
      Router.navigate('/shipping/withEncoding/:opCode', { opCode: this.opCode, ...filterParams })
    } else {
      Router.navigate(this.backPath, { opCode: this.opCode, ...filterParams })
    }
  }

  render() {
    const {
      parcels,
      loading,
      asn,
      filteredParcelReadings,
      currentTabOptions,
      filteredProducts,
      filteredChecklist,
      loadingData,
      currentListPage,
      totalPages
    } = this.state
    const activeConfirmShipment = !!(parcels && parcels.length > 0)
    const counter = this.getAsnCounter()

    let askCloseConfirm = false
    if (activeConfirmShipment && parcels) {
      askCloseConfirm = parcels.some((parcel) => !parcel.parcelEntryQuantity)
    }

    return (
      <Page
        headerRight={
          <ParcelRightHeader
            shipmentCode={asn?.code}
            onConfirmShpment={this.getOnConfirmShipmentCB(askCloseConfirm)}
            hideConfirmShipment={
              ['RECEIVED', 'FORCE_RECEIVED'].includes(asn?.status ?? '') ||
              !(this.operation.shippingMode === 'shipment' || !this.operation.autoconfirmAsnOnParcelConfirmation)
            }
            activeConfirmShipment={activeConfirmShipment}
            totalExpecteds={counter.expected}
            totalReadings={counter.detected}
            canDelete={this.canDelete()}
            deleteFunc={asn ? () => this.deleteFunc(asn.id, 'shipment') : undefined}
          />
        }
        title={this.operation.description}
        onBackPress={this.goBack}
      >
        <Box bg={'#FFF'} flex>
          <Page.Content notBoxed>
            <Box p={0} pb={0} mb={40} row justify={'space-between'}>
              <Box row vcenter style={{ gap: '20px' }}>
                {this.operation.options.checklistMode === 'shipment' && asn?.checklist && (
                  <Tab
                    variant="secondary"
                    type="square"
                    options={currentTabOptions}
                    onOptionSelected={(opt) => this.onTabClick(opt)}
                  />
                )}
                {currentTabOptions.find((opt) => opt.value === 'parcels')?.active && (
                  <Input
                    onChange={this.filterAsnParcel}
                    placeholder={`${__(T.misc.parcel)}`}
                    image={<Icons.Barcode />}
                    style={{ width: 325 }}
                    // autoFocus - blur bug
                    debounce={300}
                    disabled={loading}
                  />
                )}
                {currentTabOptions.find((opt) => opt.value === 'products')?.active && (
                  <Input
                    onChange={this.filterAsnProduct}
                    placeholder={`${__(T.misc.product)}`}
                    image={<Icons.Barcode />}
                    style={{ width: 325 }}
                    // autoFocus - blur bug
                    debounce={300}
                    disabled={loading}
                  />
                )}
              </Box>
              {this.canShowAddParcel() && (
                <Button title={`${__(T.misc.new_parcel)}`} disabled={false} onClick={this.onNewParcel} />
              )}
            </Box>
            {currentTabOptions.find((opt) => opt.value === 'parcels')?.active && (
              <List
                title={`${__(T.misc.parcels)} (${parcels?.length ?? 0})`}
                loading={loading}
                data={parcels}
                loadMore={this.loadMore}
                isNextPageLoading={loadingData}
                stopLoadMore={currentListPage === totalPages - 1}
                rowRender={(parcel: ShippingParcel, index: number, listRef: any, onRowHeightChange?: () => void) => {
                  return this.parcelRowRender(parcel, index, listRef, onRowHeightChange)
                }}
              />
            )}
            {currentTabOptions.find((opt) => opt.value === 'products')?.active && (
              <ShippingProductTable
                readings={filteredParcelReadings}
                operation={this.operation}
                extension={ShippingExtensions}
                checklist={filteredChecklist}
                products={filteredProducts}
                productKey={this.operation.options.checklistMode === 'shipment' ? 'code' : undefined}
                loading={this.state.loadingReadings}
              />
            )}
          </Page.Content>
        </Box>
      </Page>
    )
  }
}
